import React from 'react'

import { cn } from 'src/helpers/style'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ISkeletonBaseProps {
  /** Extra classes to override width / height / rounding etc. */
  className?: string
  /** Accessible label announced by screen readers (defaults to "Loading…") */
  'aria-label'?: string
}

export interface ISkeletonTableRowsProps {
  /** How many body rows to render */
  rows?: number
  className?: string
}

export interface ISkeletonSummaryCardsProps {
  /** Number of summary cards (default 4 — matches Fund summary) */
  count?: number
  className?: string
}

// ─── Base ─────────────────────────────────────────────────────────────────────

/**
 * Lowest-level building block. Renders a shimmering placeholder box.
 */
const SkeletonBase = ({
  className,
  'aria-label': ariaLabel = 'Loading…',
}: ISkeletonBaseProps): JSX.Element => (
  <span
    role="status"
    aria-label={ariaLabel}
    aria-busy="true"
    className={cn('block rounded-sm animate-skeleton', className)}
  />
)

// ─── Shared header label ───────────────────────────────────────────────────────

/** Thin pill that mimics a table `<th>` label */
const HeaderLabel = ({ className }: { className?: string }): JSX.Element => (
  <SkeletonBase className={cn('h-2.5 rounded-sm opacity-60', className)} />
)

// ─── Insights table (Member insights / Fund updates) ──────────────────────────
// Columns: Report Name (wide) | Category | Content Coverage | Producers | Release Date | Download icon

/**
 * A single skeleton row matching the InsightsTable layout.
 * Proportions mirror the real `insights-table.tsx` column widths.
 */
const SkeletonInsightsRow = (): JSX.Element => (
  <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] py-8">
    {/* Report Name — wide primary text */}
    <SkeletonBase className="h-3.5 w-[35%] shrink-0" />

    {/* Category */}
    <SkeletonBase className="h-3 w-[12%] shrink-0 opacity-70" />

    {/* Content Coverage */}
    <SkeletonBase className="h-3 w-[14%] shrink-0 opacity-70" />

    {/* Producers */}
    <SkeletonBase className="h-3 w-[12%] shrink-0 opacity-70" />

    {/* Release Date */}
    <SkeletonBase className="h-3 w-[10%] shrink-0 opacity-70" />

    {/* Download icon placeholder */}
    <SkeletonBase className="ml-auto h-4 w-4 shrink-0 rounded opacity-50" />
  </div>
)

/**
 * Full Insights table skeleton — matches the `<InsightsTable>` component used
 * in Member Insights and Fund Updates pages.
 *
 * Includes the column header row + `rows` body rows.
 */
const SkeletonInsightsTable = ({
  rows = 6,
  className,
}: ISkeletonTableRowsProps): JSX.Element => (
  <div
    role="status"
    aria-label="Loading insights…"
    aria-busy="true"
    className={cn('w-full', className)}
  >
    {/* Header row */}
    <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] pb-3">
      <HeaderLabel className="w-[35%] shrink-0" />
      <HeaderLabel className="w-[12%] shrink-0" />
      <HeaderLabel className="w-[14%] shrink-0" />
      <HeaderLabel className="w-[12%] shrink-0" />
      <HeaderLabel className="w-[10%] shrink-0" />
      <HeaderLabel className="ml-auto w-4 shrink-0" />
    </div>

    {/* Body rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonInsightsRow key={i} />
    ))}
  </div>
)

// ─── Fund updates — document table ────────────────────────────────────────────
// Columns: Document (wide) | Category | Date — matches Fund updates `Table` columns

const SkeletonDocumentUpdatesRow = (): JSX.Element => (
  <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] py-8">
    <SkeletonBase className="h-3.5 w-[47%] shrink-0" />
    <SkeletonBase className="h-3 w-[26%] shrink-0 opacity-70" />
    <SkeletonBase className="h-3 w-[26%] shrink-0 opacity-70" />
  </div>
)

/**
 * Fund Updates documents table skeleton — three columns, aligned with the real table.
 */
const SkeletonDocumentUpdatesTable = ({
  rows = 4,
  className,
}: ISkeletonTableRowsProps): JSX.Element => (
  <div
    role="status"
    aria-label="Loading documents…"
    aria-busy="true"
    className={cn('w-full', className)}
  >
    <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] pb-3">
      <HeaderLabel className="w-[47%] shrink-0" />
      <HeaderLabel className="w-[26%] shrink-0" />
      <HeaderLabel className="w-[26%] shrink-0" />
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonDocumentUpdatesRow key={i} />
    ))}
  </div>
)

// ─── Portfolio-news table ─────────────────────────────────────────────────────
// Columns: Article name (very wide) | Company | Source | Published

/**
 * A single skeleton row matching the Portfolio News list layout.
 */
const SkeletonNewsRow = (): JSX.Element => (
  <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] py-8">
    {/* Article name — takes most of the width */}
    <SkeletonBase className="h-3.5 w-[50%] shrink-0" />

    {/* Company */}
    <SkeletonBase className="h-3 w-[14%] shrink-0 opacity-70" />

    {/* Source */}
    <SkeletonBase className="h-3 w-[14%] shrink-0 opacity-70" />

    {/* Published */}
    <SkeletonBase className="ml-auto h-3 w-[10%] shrink-0 opacity-70" />
  </div>
)

/**
 * Full Portfolio News table skeleton — matches the news list layout.
 *
 * Includes the column header row + `rows` body rows.
 */
const SkeletonNewsTable = ({
  rows = 6,
  className,
}: ISkeletonTableRowsProps): JSX.Element => (
  <div
    role="status"
    aria-label="Loading news…"
    aria-busy="true"
    className={cn('w-full', className)}
  >
    {/* Header row */}
    <div className="flex items-center gap-6 border-b border-[var(--color-border-default)] pb-3">
      <HeaderLabel className="w-[50%] shrink-0" />
      <HeaderLabel className="w-[14%] shrink-0" />
      <HeaderLabel className="w-[14%] shrink-0" />
      <HeaderLabel className="ml-auto w-[10%] shrink-0" />
    </div>

    {/* Body rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonNewsRow key={i} />
    ))}
  </div>
)

// ─── Overview — Fund Summary cards ────────────────────────────────────────────
// Mobile: 2×2 rounded card with internal dividers, 12px padding, 28px value.
// Desktop (md+): 4 cards in a row with 2px left dividers, no outer card chrome.

/**
 * Mimics the "Fund summary" section on the Overview page.
 * Renders `count` cards (default 4) that match the metric-card proportions.
 */
const SkeletonSummaryCards = ({
  count = 4,
  className,
}: ISkeletonSummaryCardsProps): JSX.Element => (
  <div
    role="status"
    aria-label="Loading summary…"
    aria-busy="true"
    className={cn(
      'rounded-[20px] border border-border-default md:rounded-none md:border-0',
      className,
    )}
  >
    <div className="grid w-full grid-cols-2 md:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => {
        const isRightColumnOnMobile = i % 2 === 1
        const isBottomRowOnMobile = i >= 2
        const isFirstOnDesktop = i === 0

        return (
          <div
            key={i}
            className={cn(
              'flex flex-col gap-1 p-3',
              isRightColumnOnMobile && 'border-l border-border-default',
              isBottomRowOnMobile && 'border-t border-border-default',
              'md:min-h-[104px] md:justify-between md:gap-2 md:py-1 md:pl-5 md:pr-5 md:first:pl-0',
              isBottomRowOnMobile && 'md:border-t-0',
              isFirstOnDesktop
                ? 'md:border-l-0'
                : 'md:border-l-2 md:border-border-default',
            )}
          >
            {/* Label — e.g. "Total commitment" */}
            <SkeletonBase className="h-2.5 w-24 opacity-60" />

            {/* Big value — e.g. "$50.0M" */}
            <SkeletonBase className="h-7 w-24 md:h-8 md:w-28" />

            {/* Sub-label — e.g. "Fund II" */}
            <SkeletonBase className="h-2.5 w-20 opacity-50" />
          </div>
        )
      })}
    </div>
  </div>
)

// ─── Overview — Recent news / New documents simple list ───────────────────────
// Each row: title text left + date right (no extra columns)

export interface ISkeletonSimpleListProps {
  count?: number
  className?: string
}

/**
 * Mimics the "Recent news" and "New documents" lists on the Overview page.
 * Each row is: primary text (left) + relative date (right), separated by a divider.
 */
const SkeletonSimpleList = ({
  count = 3,
  className,
}: ISkeletonSimpleListProps): JSX.Element => (
  <div
    role="status"
    aria-label="Loading list…"
    aria-busy="true"
    className={cn(
      'w-full divide-y divide-border-default border-y border-border-default',
      className,
    )}
  >
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex h-[84px] items-center gap-4 px-3">
        {/* Article / document title */}
        <SkeletonBase className="h-3 w-[65%]" />

        {/* Relative date — right-aligned */}
        <SkeletonBase className="ml-auto h-3 w-8 opacity-60" />
      </div>
    ))}
  </div>
)

// ─── Compound export ───────────────────────────────────────────────────────────

/**
 * Compound skeleton component.
 *
 * @example
 * // Raw block
 * <Skeleton.Base className="h-6 w-32" />
 *
 * // Member Insights / Fund Updates table
 * <Skeleton.InsightsTable rows={6} />
 *
 * // Portfolio News table
 * <Skeleton.NewsTable rows={6} />
 *
 * // Fund Updates — documents table (3 columns)
 * <Skeleton.DocumentUpdatesTable rows={4} />
 *
 * // Overview — Fund Summary cards
 * <Skeleton.SummaryCards count={4} />
 *
 * // Overview — Recent news or New documents mini list
 * <Skeleton.SimpleList count={3} />
 */
export const Skeleton = Object.assign(
  /** Alias for Skeleton.Base to allow <Skeleton className="…" /> shorthand */
  SkeletonBase,
  {
    Base: SkeletonBase,
    InsightsTable: SkeletonInsightsTable,
    InsightsRow: SkeletonInsightsRow,
    NewsTable: SkeletonNewsTable,
    NewsRow: SkeletonNewsRow,
    DocumentUpdatesTable: SkeletonDocumentUpdatesTable,
    DocumentUpdatesRow: SkeletonDocumentUpdatesRow,
    SummaryCards: SkeletonSummaryCards,
    SimpleList: SkeletonSimpleList,
  },
)
